import { isEmpty } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';

import { useSearchParamsStable } from '../hooks/useSearchParams';

type KeyBoundSearchInputRegistryValue = {
  register: (key: string) => void;
  unregister: (key: string) => void;
  getIndex: (key: string) => number;
};

const KeyBoundSearchInputRegistryContext = createContext<KeyBoundSearchInputRegistryValue | null>(
  null,
);

function utf8ToBase64(str: string) {
  return window.btoa(encodeURIComponent(str));
}

function base64ToUTF8(str: string) {
  return decodeURIComponent(window.atob(str));
}

type Serializable = string | number | boolean | object | null;

type Updater<T, O = T> = O | ((prev: T) => O);

interface SearchParamsStateContextValue {
  keys: { [key: string]: Serializable };
  setKey<T extends Serializable>(
    key: string,
    updater: Updater<T | null>,
    defaultValue: T | undefined,
  ): void;
  getNextSearchParams<T extends Serializable>(
    key: string,
    updater: Updater<T | null>,
    defaultValue: T | undefined,
  ): URLSearchParams;
}

const SearchParamsStateContext = createContext<SearchParamsStateContextValue>(null as any);

const SEARCH_PARAMS_STATE_KEY = 's';

function serializeState(state: Record<string, Serializable | undefined>) {
  return !isEmpty(state) ? utf8ToBase64(JSON.stringify(state)) : null;
}

export function getSearchParamsStateParamsObject(state: Record<string, Serializable | undefined>) {
  return {
    [SEARCH_PARAMS_STATE_KEY]: serializeState(state),
  };
}

function deserializeState(serializedVal: string) {
  return JSON.parse(base64ToUTF8(serializedVal));
}

function getNextState<T>(
  key: string,
  prev: { [p: string]: Serializable },
  updater: Updater<T, T | null>,
  defaultValue: T | undefined,
) {
  const nextValue =
    updater instanceof Function ? updater((prev[key] ?? defaultValue) as any) : updater;

  if (nextValue === null) {
    const { [key]: skip, ...rest } = prev;
    return rest;
  }

  return {
    ...prev,
    [key]: nextValue,
  };
}

type SearchParamsState = { [key: string]: Serializable };

export function SearchParamsStateProvider({ children }: { children?: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParamsStable();
  const [searchInputKeys, setSearchInputKeys] = useState<Set<string>>(() => new Set());

  const [state, innerSetState] = useState<SearchParamsState>(() => {
    const serializedVal = searchParams.get(SEARCH_PARAMS_STATE_KEY);

    if (serializedVal) {
      return deserializeState(serializedVal);
    }

    return {};
  });

  const [debouncedState, { flush }] = useDebounce(state, 200);

  const setState = useCallback((updater: Updater<SearchParamsState>) => {
    innerSetState((prevState) => {
      const newState = typeof updater === 'function' ? updater(prevState) : updater;
      return newState;
    });
  }, []);

  useEffect(() => {
    setSearchParams(
      (params) => {
        const newSearchParams = new URLSearchParams(params);
        const serializedState = serializeState(debouncedState);
        if (serializedState) {
          newSearchParams.set(SEARCH_PARAMS_STATE_KEY, serializedState);
        } else {
          newSearchParams.delete(SEARCH_PARAMS_STATE_KEY);
        }

        return newSearchParams;
      },
      { replace: true },
    );
  }, [debouncedState, setSearchParams]);

  useEffect(() => {
    const serializedVal = searchParams.get(SEARCH_PARAMS_STATE_KEY);

    if (serializedVal === null) {
      innerSetState({});
    } else if (serializedVal !== serializeState(debouncedState)) {
      innerSetState(deserializeState(serializedVal));
    }

    flush();
    // Ignoring debouncedState changes here intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setKey = useCallback(
    <T extends Serializable>(
      key: string,
      updater: Updater<T, T | null>,
      defaultValue: T | undefined,
    ) => {
      setState((prev = {}) => getNextState(key, prev, updater, defaultValue));
    },
    [setState],
  );

  const searchInputRegister = useCallback((key: string) => {
    setSearchInputKeys((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);

  const searchInputUnregister = useCallback((key: string) => {
    setSearchInputKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const searchInputGetIndex = useCallback(
    (key: string) => {
      const sorted = Array.from(searchInputKeys).sort();
      const idx = sorted.indexOf(key);
      return idx >= 0 ? idx : 0;
    },
    [searchInputKeys],
  );

  const searchInputRegistryValue = useMemo(
    () => ({
      register: searchInputRegister,
      unregister: searchInputUnregister,
      getIndex: searchInputGetIndex,
    }),
    [searchInputRegister, searchInputUnregister, searchInputGetIndex],
  );

  const value = useMemo(
    () => ({
      keys: state,
      getNextSearchParams<T extends Serializable>(
        key: string,
        updater: Updater<T | null>,
        defaultValue: T | undefined,
      ) {
        const nextState = serializeState(getNextState(key, state, updater, defaultValue));
        const nextParams = new URLSearchParams(searchParams);

        if (nextState) {
          nextParams.set(SEARCH_PARAMS_STATE_KEY, nextState);
        } else {
          nextParams.delete(SEARCH_PARAMS_STATE_KEY);
        }

        return nextParams;
      },
      setKey,
    }),
    [searchParams, state, setKey],
  );

  return (
    <SearchParamsStateContext.Provider value={value}>
      <KeyBoundSearchInputRegistryContext.Provider value={searchInputRegistryValue}>
        {children}
      </KeyBoundSearchInputRegistryContext.Provider>
    </SearchParamsStateContext.Provider>
  );
}

export function useKeyBoundSearchInputRegistry() {
  return useContext(KeyBoundSearchInputRegistryContext);
}

export function useSearchParamsStateContext() {
  return useContext(SearchParamsStateContext);
}

export function useSearchParamsState<T extends Serializable>(
  key: string,
): [
  T | undefined,
  (updater: Updater<T | undefined> | null) => void,
  (updater: Updater<T | undefined>) => URLSearchParams,
];
export function useSearchParamsState<T extends Serializable>(
  key: string,
  defaultValue: T,
): [T, (updater: Updater<T> | null) => void, (updater: Updater<T>) => URLSearchParams];
export function useSearchParamsState<T extends Serializable>(
  key: string,
  defaultValue?: T,
): [
  T | undefined,
  (updater: Updater<T | undefined> | null) => void,
  (updater: Updater<T | undefined>) => URLSearchParams,
];
export function useSearchParamsState<T extends Serializable>(
  key: string,
  defaultValue?: T,
): [
  T | undefined,
  (updater: Updater<T | undefined, T> | null) => void,
  (updater: Updater<T | undefined, T>) => URLSearchParams,
] {
  const ctx = useContext(SearchParamsStateContext);

  if (!ctx) {
    throw new Error('useSearchParamsState must be used within SearchParamsStateProvider');
  }

  const { setKey } = ctx;

  const setValue = useCallback(
    (updater: Updater<T> | null) => {
      if (updater === defaultValue || updater === null) {
        return setKey(key, null, defaultValue);
      }

      return setKey(key, updater as Updater<T | null>, defaultValue);
    },
    [setKey, defaultValue, key],
  );

  const getNextSearchParams = useCallback(
    (updater: Updater<T>) => {
      if (updater === defaultValue) {
        return ctx.getNextSearchParams(key, null, defaultValue);
      }

      return ctx.getNextSearchParams(key, updater as Updater<T | null>, defaultValue);
    },
    [ctx, defaultValue, key],
  );

  return useMemo(
    () => [(ctx.keys[key] ?? defaultValue) as T | undefined, setValue, getNextSearchParams],
    [ctx.keys, defaultValue, getNextSearchParams, key, setValue],
  );
}

export function useSyncToSearchParams<T extends Serializable>(
  key: string,
  value: T,
  { enabled = false }: { enabled?: boolean },
) {
  const [, setSearchParamsValue] = useSearchParamsState<T>(key);
  const setValue = useEffectEvent(setSearchParamsValue);
  useEffect(() => {
    if (enabled) {
      setValue(value);
    }
  }, [value, enabled]);
}
